import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Container,
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  Grid,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import {
  ArrowBack,
  AccessTime,
  CalendarToday,
  Assessment,
  Refresh,
  Delete,
  PictureAsPdf,
  ExpandMore,
} from "@mui/icons-material";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import Header from "../components/Layout/Header";
import ComponentDiagram from "../components/Dashboard/ComponentDiagram";
import { dashboardAPI } from "../services/api";
import { sanitizeHtml } from "../utils/htmlUtils";

// Helper component to format and display impact text
const FormattedImpactText = ({ text }) => {
  if (!text) return null;

  // Split text into paragraphs
  const paragraphs = text.split(/\n\s*\n/).filter((p) => p.trim());

  const formatInlineText = (text) => {
    // Split by common technical terms and format
    const parts = [];
    const regex =
      /\b(component|service|module|file|function|class|API|database|endpoint|must|should|will|need to|required|important)\b/gi;
    let lastIndex = 0;
    let match;

    while ((match = regex.exec(text)) !== null) {
      // Add text before match
      if (match.index > lastIndex) {
        parts.push(text.substring(lastIndex, match.index));
      }
      // Add highlighted match
      parts.push(
        <Box
          key={`${match.index}-${match[0]}`}
          component="strong"
          sx={{ fontWeight: 600, color: "text.primary" }}
        >
          {match[0]}
        </Box>,
      );
      lastIndex = regex.lastIndex;
    }

    // Add remaining text
    if (lastIndex < text.length) {
      parts.push(text.substring(lastIndex));
    }

    return parts.length > 0 ? parts : text;
  };

  return (
    <Box>
      {paragraphs.map((para, paraIndex) => {
        const lines = para.split("\n").filter((l) => l.trim());
        const listItems = [];
        const regularLines = [];

        lines.forEach((line, lineIndex) => {
          if (/^[-•*]\s+/.test(line) || /^\d+\.\s+/.test(line)) {
            const content = line
              .replace(/^[-•*]\d+\.\s+/, "")
              .replace(/^[-•*]\s+/, "");
            listItems.push(
              <Box
                key={lineIndex}
                component="li"
                sx={{ mb: 0.5, lineHeight: 1.8 }}
              >
                {formatInlineText(content)}
              </Box>,
            );
          } else {
            regularLines.push(
              <Typography
                key={lineIndex}
                variant="body1"
                sx={{ mb: 1.5, lineHeight: 1.8 }}
              >
                {formatInlineText(line)}
              </Typography>,
            );
          }
        });

        return (
          <Box key={paraIndex}>
            {listItems.length > 0 && (
              <Box component="ul" sx={{ pl: 3, mb: 2, listStyleType: "disc" }}>
                {listItems}
              </Box>
            )}
            {regularLines}
          </Box>
        );
      })}
    </Box>
  );
};

const ChangeRequestDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [changeRequest, setChangeRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    loadChangeRequest();
  }, [id]);

  const loadChangeRequest = async () => {
    try {
      setError("");
      setSuccess("");
      const response = await dashboardAPI.getChangeRequest(id);
      setChangeRequest(response.data);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to load change request");
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerate = async () => {
    if (
      !window.confirm(
        "Are you sure you want to regenerate the AI analysis? This will update the functional impact, technical impact, estimates, and component structure.",
      )
    ) {
      return;
    }

    setRegenerating(true);
    setError("");
    setSuccess("");

    try {
      const response = await dashboardAPI.regenerateChangeRequest(id);
      setChangeRequest(response.data);
      setSuccess("Change request analysis regenerated successfully!");
    } catch (err) {
      setError(
        err.response?.data?.error || "Failed to regenerate change request",
      );
    } finally {
      setRegenerating(false);
    }
  };

  const handleBack = () => {
    // Check if we have location state with tab info (from dashboard)
    if (location.state?.tab !== undefined) {
      navigate(`/dashboard?tab=${location.state.tab}`);
    } else {
      // Use browser history to go back, or fallback to dashboard
      if (window.history.length > 1) {
        navigate(-1);
      } else {
        navigate("/dashboard");
      }
    }
  };

  const handleDelete = async () => {
    if (
      !window.confirm(
        "Are you sure you want to delete this change request? This action cannot be undone.",
      )
    ) {
      return;
    }

    try {
      await dashboardAPI.deleteChangeRequest(id);
      handleBack();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to delete change request");
    }
  };

  const handleGeneratePDF = async () => {
    setGeneratingPDF(true);
    setError("");

    try {
      // Hide React Flow controls and minimap before capturing
      const reactFlowControls = document.querySelector(".react-flow__controls");
      const reactFlowMinimap = document.querySelector(".react-flow__minimap");
      const reactFlowAttribution = document.querySelector(
        ".react-flow__attribution",
      );

      const originalControlsDisplay = reactFlowControls?.style.display;
      const originalMinimapDisplay = reactFlowMinimap?.style.display;
      const originalAttributionDisplay = reactFlowAttribution?.style.display;

      if (reactFlowControls) reactFlowControls.style.display = "none";
      if (reactFlowMinimap) reactFlowMinimap.style.display = "none";
      if (reactFlowAttribution) reactFlowAttribution.style.display = "none";

      // Wait a bit for DOM to update
      await new Promise((resolve) => setTimeout(resolve, 100));

      // Get the main content container
      const element = document.getElementById("change-request-content");
      if (!element) {
        throw new Error("Content element not found");
      }

      // Get diagram element separately if it exists
      const diagramElement = element.querySelector('[class*="react-flow"]');
      let diagramCanvas = null;
      let diagramImgData = null;

      if (diagramElement) {
        // Capture diagram separately
        diagramCanvas = await html2canvas(diagramElement, {
          scale: 2,
          useCORS: true,
          logging: false,
          backgroundColor: "#ffffff",
          width: diagramElement.scrollWidth,
          height: diagramElement.scrollHeight,
        });
        diagramImgData = diagramCanvas.toDataURL("image/png");
      }

      // Hide diagram temporarily to capture rest of content
      const originalDiagramDisplay = diagramElement?.style.display;
      if (diagramElement) {
        diagramElement.style.display = "none";
      }

      // Create canvas from the rest of the content
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff",
        width: element.scrollWidth,
        height: element.scrollHeight,
      });

      const imgData = canvas.toDataURL("image/png");

      // Restore diagram display
      if (diagramElement) {
        diagramElement.style.display = originalDiagramDisplay || "";
      }

      // Restore React Flow controls
      if (reactFlowControls)
        reactFlowControls.style.display = originalControlsDisplay || "";
      if (reactFlowMinimap)
        reactFlowMinimap.style.display = originalMinimapDisplay || "";
      if (reactFlowAttribution)
        reactFlowAttribution.style.display = originalAttributionDisplay || "";

      // Calculate PDF dimensions
      const pdfWidth = 210; // A4 width in mm
      const pdfHeight = 297; // A4 height in mm
      const margin = 10; // mm margin on all sides
      const contentWidth = pdfWidth - 2 * margin;
      const contentHeight = pdfHeight - 2 * margin;

      // Create PDF
      const pdf = new jsPDF("p", "mm", "a4");

      // Add main content with proper page breaks
      const mainImgWidth = contentWidth;
      const mainImgHeight = (canvas.height * mainImgWidth) / canvas.width;
      let mainHeightLeft = mainImgHeight;
      let mainPosition = 0;
      let pageAdded = false;

      // Add first page with main content
      pdf.addImage(
        imgData,
        "PNG",
        margin,
        margin + mainPosition,
        mainImgWidth,
        mainImgHeight,
      );
      mainHeightLeft -= contentHeight;
      pageAdded = true;

      // Add additional pages for main content if needed
      while (mainHeightLeft > 0) {
        mainPosition = -mainImgHeight + mainHeightLeft + contentHeight;
        pdf.addPage();
        pdf.addImage(
          imgData,
          "PNG",
          margin,
          margin + mainPosition,
          mainImgWidth,
          mainImgHeight,
        );
        mainHeightLeft -= contentHeight;
      }

      // Add diagram on a new page if it exists
      if (diagramImgData && diagramCanvas) {
        pdf.addPage();

        // Calculate diagram dimensions to fit on page
        const diagramAspectRatio = diagramCanvas.width / diagramCanvas.height;
        let diagramPdfWidth = contentWidth;
        let diagramPdfHeight = diagramPdfWidth / diagramAspectRatio;

        // If diagram is too tall, use landscape orientation for this page
        if (diagramPdfHeight > contentHeight) {
          // Switch to landscape for diagram page
          pdf.addPage([297, 210], "l"); // landscape A4
          const landscapeWidth = 277; // landscape width - margins
          const landscapeHeight = 190; // landscape height - margins

          diagramPdfWidth = landscapeWidth;
          diagramPdfHeight = diagramPdfWidth / diagramAspectRatio;

          // If still too tall, scale to fit height
          if (diagramPdfHeight > landscapeHeight) {
            diagramPdfHeight = landscapeHeight;
            diagramPdfWidth = diagramPdfHeight * diagramAspectRatio;
          }

          // Center the diagram
          const xPos = (297 - diagramPdfWidth) / 2;
          const yPos = (210 - diagramPdfHeight) / 2;

          pdf.addImage(
            diagramImgData,
            "PNG",
            xPos,
            yPos,
            diagramPdfWidth,
            diagramPdfHeight,
          );
        } else {
          // Portrait orientation - center the diagram
          const xPos = (pdfWidth - diagramPdfWidth) / 2;
          const yPos = (pdfHeight - diagramPdfHeight) / 2;

          pdf.addImage(
            diagramImgData,
            "PNG",
            xPos,
            yPos,
            diagramPdfWidth,
            diagramPdfHeight,
          );
        }
      }

      // Save PDF
      const fileName = `ChangeRequest_${changeRequest.title.replace(/[^a-z0-9]/gi, "_")}_${new Date().toISOString().split("T")[0]}.pdf`;
      pdf.save(fileName);

      setSuccess("PDF generated successfully!");
    } catch (err) {
      console.error("PDF generation error:", err);
      setError("Failed to generate PDF: " + (err.message || "Unknown error"));

      // Restore controls in case of error
      const reactFlowControls = document.querySelector(".react-flow__controls");
      const reactFlowMinimap = document.querySelector(".react-flow__minimap");
      const reactFlowAttribution = document.querySelector(
        ".react-flow__attribution",
      );
      if (reactFlowControls) reactFlowControls.style.display = "";
      if (reactFlowMinimap) reactFlowMinimap.style.display = "";
      if (reactFlowAttribution) reactFlowAttribution.style.display = "";
    } finally {
      setGeneratingPDF(false);
    }
  };

  if (loading) {
    return (
      <Box>
        <Header />
        <Container maxWidth="lg" sx={{ mt: 4 }}>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="50vh"
          >
            <CircularProgress />
          </Box>
        </Container>
      </Box>
    );
  }

  if (error || !changeRequest) {
    return (
      <Box>
        <Header />
        <Container maxWidth="lg" sx={{ mt: 4 }}>
          <Alert severity="error">{error || "Change request not found"}</Alert>
          <Button startIcon={<ArrowBack />} onClick={handleBack} sx={{ mt: 2 }}>
            Back
          </Button>
        </Container>
      </Box>
    );
  }

  return (
    <Box>
      <Header />
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Button startIcon={<ArrowBack />} onClick={handleBack} sx={{ mb: 2 }}>
          Back
        </Button>

        <Paper sx={{ p: 4 }} id="change-request-content">
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert
              severity="success"
              sx={{ mb: 2 }}
              onClose={() => setSuccess("")}
            >
              {success}
            </Alert>
          )}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="flex-start"
            mb={3}
          >
            <Box flex={1}>
              <Typography variant="h4" component="h1" gutterBottom>
                {changeRequest.title}
              </Typography>
              <Box display="flex" gap={1} flexWrap="wrap" mt={1}>
                <Chip
                  label={changeRequest.status}
                  color="primary"
                  size="small"
                />
                {changeRequest.estimated_hours && (
                  <Chip
                    icon={<AccessTime />}
                    label={`${changeRequest.estimated_hours} hours`}
                    size="small"
                  />
                )}
                {changeRequest.estimated_days && (
                  <Chip
                    icon={<CalendarToday />}
                    label={`${changeRequest.estimated_days} days`}
                    size="small"
                  />
                )}
                {changeRequest.estimated_story_points && (
                  <Chip
                    icon={<Assessment />}
                    label={`${changeRequest.estimated_story_points} Story Points`}
                    size="small"
                  />
                )}
              </Box>
            </Box>
            <Box
              display="flex"
              flexDirection="column"
              alignItems="flex-end"
              gap={1}
            >
              <Typography variant="body2" color="text.secondary">
                Created: {new Date(changeRequest.created_at).toLocaleString()}
              </Typography>
              {changeRequest.updated_at &&
                changeRequest.updated_at !== changeRequest.created_at && (
                  <Typography variant="body2" color="text.secondary">
                    Updated:{" "}
                    {new Date(changeRequest.updated_at).toLocaleString()}
                  </Typography>
                )}
              <Box display="flex" gap={1}>
                <Button
                  variant="contained"
                  startIcon={<PictureAsPdf />}
                  onClick={handleGeneratePDF}
                  disabled={generatingPDF}
                  color="primary"
                >
                  {generatingPDF ? "Generating PDF..." : "Generate PDF"}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<Refresh />}
                  onClick={handleRegenerate}
                  disabled={regenerating}
                  color="primary"
                >
                  {regenerating ? "Regenerating..." : "Regenerate AI Analysis"}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<Delete />}
                  onClick={handleDelete}
                  color="error"
                >
                  Delete
                </Button>
              </Box>
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Description
              </Typography>
              {changeRequest.description ? (
                <Box
                  sx={{
                    maxHeight: 500,
                    overflowY: "auto",
                    p: 2,
                    border: "1px solid #eee",
                    borderRadius: "4px",
                    wordBreak: "break-word",
                    "& p": {
                      margin: "0.5em 0",
                      lineHeight: 1.6,
                    },
                    "& div": {
                      margin: "0.5em 0",
                      lineHeight: 1.6,
                    },
                    "& br": {
                      display: "block",
                      margin: "0.25em 0",
                    },
                    "& b, & strong": {
                      fontWeight: "bold",
                    },
                    "& u": {
                      textDecoration: "underline",
                    },
                    "& ul, & ol": {
                      paddingLeft: "1.5em",
                      margin: "0.5em 0",
                    },
                    "& li": {
                      margin: "0.25em 0",
                    },
                    "& img": {
                      maxWidth: "100%",
                      height: "auto",
                      display: "block",
                      margin: "1em 0",
                      borderRadius: "4px",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                    },
                    "& table": {
                      width: "100%",
                      borderCollapse: "collapse",
                      margin: "1em 0",
                    },
                    "& th, & td": {
                      border: "1px solid #ddd",
                      padding: "0.5em",
                      textAlign: "left",
                    },
                    "& th": {
                      backgroundColor: "#f5f5f5",
                      fontWeight: "bold",
                    },
                  }}
                  dangerouslySetInnerHTML={{
                    __html: sanitizeHtml(changeRequest.description),
                  }}
                />
              ) : (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontStyle: "italic" }}
                >
                  No description provided
                </Typography>
              )}
            </Grid>

            {changeRequest.template_content && (
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Change Request Template
                </Typography>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: "grey.50" }}>
                  <Box
                    sx={{
                      wordBreak: "break-word",
                      "& p": {
                        margin: "0.5em 0",
                        lineHeight: 1.6,
                      },
                      "& div": {
                        margin: "0.5em 0",
                        lineHeight: 1.6,
                      },
                      "& br": {
                        display: "block",
                        margin: "0.25em 0",
                      },
                      "& b, & strong": {
                        fontWeight: "bold",
                      },
                      "& u": {
                        textDecoration: "underline",
                      },
                      "& ul, & ol": {
                        paddingLeft: "1.5em",
                        margin: "0.5em 0",
                      },
                      "& li": {
                        margin: "0.25em 0",
                      },
                      "& img": {
                        maxWidth: "100%",
                        height: "auto",
                        display: "block",
                        margin: "1em 0",
                        borderRadius: "4px",
                        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                      },
                    }}
                    dangerouslySetInnerHTML={{
                      __html: sanitizeHtml(changeRequest.template_content),
                    }}
                  />
                </Paper>
              </Grid>
            )}

            {changeRequest.functional_impact && (
              <Grid item xs={12} md={6}>
                <Accordion
                  defaultExpanded={false}
                  sx={{
                    borderLeft: "4px solid",
                    borderLeftColor: "primary.main",
                    "&:before": {
                      display: "none",
                    },
                    boxShadow: 1,
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMore />}
                    sx={{
                      bgcolor: "background.paper",
                      "&:hover": {
                        bgcolor: "action.hover",
                      },
                    }}
                  >
                    <Typography
                      variant="h6"
                      color="primary"
                      fontWeight="bold"
                    >
                      Functional Impact Analysis
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <FormattedImpactText
                      text={changeRequest.functional_impact}
                    />
                  </AccordionDetails>
                </Accordion>
              </Grid>
            )}

            {changeRequest.technical_impact && (
              <Grid item xs={12} md={6}>
                <Accordion
                  defaultExpanded={false}
                  sx={{
                    borderLeft: "4px solid",
                    borderLeftColor: "secondary.main",
                    "&:before": {
                      display: "none",
                    },
                    boxShadow: 1,
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMore />}
                    sx={{
                      bgcolor: "background.paper",
                      "&:hover": {
                        bgcolor: "action.hover",
                      },
                    }}
                  >
                    <Typography
                      variant="h6"
                      color="secondary"
                      fontWeight="bold"
                    >
                      Technical Impact Analysis
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <FormattedImpactText
                      text={changeRequest.technical_impact}
                    />
                  </AccordionDetails>
                </Accordion>
              </Grid>
            )}

            {changeRequest.testing_impact && (
              <Grid item xs={12} md={6}>
                <Accordion
                  defaultExpanded={false}
                  sx={{
                    borderLeft: "4px solid",
                    borderLeftColor: "success.main",
                    "&:before": {
                      display: "none",
                    },
                    boxShadow: 1,
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMore />}
                    sx={{
                      bgcolor: "background.paper",
                      "&:hover": {
                        bgcolor: "action.hover",
                      },
                    }}
                  >
                    <Typography
                      variant="h6"
                      color="success.main"
                      fontWeight="bold"
                    >
                      Testing Impact Analysis
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <FormattedImpactText text={changeRequest.testing_impact} />
                  </AccordionDetails>
                </Accordion>
              </Grid>
            )}

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>
                Estimation Summary
              </Typography>
              <Grid container spacing={2} mt={1}>
                {changeRequest.estimated_hours && (
                  <Grid item xs={12} sm={4}>
                    <Paper
                      variant="outlined"
                      sx={{ p: 2, textAlign: "center" }}
                    >
                      <Typography variant="h4" color="primary">
                        {changeRequest.estimated_hours}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Hours
                      </Typography>
                    </Paper>
                  </Grid>
                )}
                {changeRequest.estimated_days && (
                  <Grid item xs={12} sm={4}>
                    <Paper
                      variant="outlined"
                      sx={{ p: 2, textAlign: "center" }}
                    >
                      <Typography variant="h4" color="primary">
                        {changeRequest.estimated_days}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Days
                      </Typography>
                    </Paper>
                  </Grid>
                )}
                {changeRequest.estimated_story_points && (
                  <Grid item xs={12} sm={4}>
                    <Paper
                      variant="outlined"
                      sx={{ p: 2, textAlign: "center" }}
                    >
                      <Typography variant="h4" color="primary">
                        {changeRequest.estimated_story_points}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Story Points
                      </Typography>
                    </Paper>
                  </Grid>
                )}
              </Grid>
            </Grid>

            {changeRequest.component_structure && (
              <Grid item xs={12}>
                <Divider sx={{ my: 3 }} />
                <ComponentDiagram
                  componentStructure={
                    typeof changeRequest.component_structure === "string"
                      ? JSON.parse(changeRequest.component_structure)
                      : changeRequest.component_structure
                  }
                />
                
                {/* File Impact Matrix */}
                {(() => {
                  const componentStructure = typeof changeRequest.component_structure === "string"
                    ? JSON.parse(changeRequest.component_structure)
                    : changeRequest.component_structure;
                  
                  // Get all components with file paths (include all types, not just "file")
                  // This ensures new files and existing files are both shown
                  const filesWithPaths = (componentStructure?.components || []).filter(
                    (comp) => comp.path && comp.path.trim() !== ""
                  );
                  
                  if (filesWithPaths.length === 0) {
                    return null;
                  }
                  
                  // Get existing file paths for comparison
                  const existingFilePathsArray = componentStructure?.existingFilePaths || [];
                  const existingFilePaths = new Set(
                    existingFilePathsArray.map(p => p.toLowerCase())
                  );
                  
                  return (
                    <Box sx={{ mt: 4 }}>
                      <Typography variant="h6" gutterBottom>
                        Code Files Impacted
                      </Typography>
                      <TableContainer component={Paper} variant="outlined" sx={{ mt: 2 }}>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: "bold" }}>File Path</TableCell>
                              <TableCell sx={{ fontWeight: "bold" }}>Component Name</TableCell>
                              <TableCell sx={{ fontWeight: "bold" }}>Impact Level</TableCell>
                              <TableCell sx={{ fontWeight: "bold" }} align="center">Status</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {filesWithPaths.map((comp, index) => {
                              const pathLower = comp.path.toLowerCase();
                              
                              // Check explicit exists field first (set by backend for new change requests)
                              let finalExists = null;
                              if (comp.exists !== undefined) {
                                finalExists = comp.exists === true || comp.exists === 'true';
                              } else if (existingFilePaths.size > 0) {
                                // Fallback: check against existingFilePaths array (for older change requests)
                                finalExists = existingFilePaths.has(pathLower);
                              }
                              
                              // Determine if it's a new file
                              const finalIsNew = finalExists === false;
                              const isUnknown = finalExists === null;
                              
                              const getImpactColor = (impactLevel) => {
                                switch (impactLevel?.toLowerCase()) {
                                  case "high":
                                    return "#f44336";
                                  case "medium":
                                    return "#ff9800";
                                  case "low":
                                    return "#2196f3";
                                  default:
                                    return "#757575";
                                }
                              };
                              
                              return (
                                <TableRow key={index} hover>
                                  <TableCell>
                                    <Tooltip title={comp.path} arrow>
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          fontFamily: "monospace",
                                          wordBreak: "break-all",
                                        }}
                                      >
                                        {comp.path}
                                      </Typography>
                                    </Tooltip>
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2">
                                      {comp.name}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    {comp.impact_level && (
                                      <Chip
                                        label={comp.impact_level.toUpperCase()}
                                        size="small"
                                        sx={{
                                          bgcolor: getImpactColor(comp.impact_level),
                                          color: "white",
                                          fontWeight: 600,
                                        }}
                                      />
                                    )}
                                  </TableCell>
                                  <TableCell align="center">
                                    {isUnknown ? (
                                      <Typography variant="body2" color="text.secondary">
                                        Unknown
                                      </Typography>
                                    ) : finalIsNew ? (
                                      <Chip
                                        label="New File"
                                        size="small"
                                        sx={{
                                          bgcolor: "#4caf50",
                                          color: "white",
                                          fontWeight: 600,
                                        }}
                                      />
                                    ) : (
                                      <Chip
                                        label="Exists"
                                        size="small"
                                        sx={{
                                          bgcolor: "#2196f3",
                                          color: "white",
                                          fontWeight: 600,
                                        }}
                                      />
                                    )}
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  );
                })()}
              </Grid>
            )}

            {changeRequest.username && (
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body2" color="text.secondary">
                  Created by: {changeRequest.username} ({changeRequest.email})
                </Typography>
              </Grid>
            )}
          </Grid>
        </Paper>
      </Container>
    </Box>
  );
};

export default ChangeRequestDetailPage;
